import {
  Autocomplete,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { HTMLAttributes, useCallback, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { VirtualizedListboxComponent } from 'components/common/VirtualizedListboxComponent'
import Icon from 'components/Icon'
import { TestSuiteFormValues } from 'components/test-suites/forms/test-suite-form-utils'
import { useTranslation } from 'hooks/use-translation'
import { Team } from 'types/api/teams'
import { naturalSortBy } from 'utils/list'

type TestSuiteTeamOption = {
  id: string
  label: string
  selected: boolean
}

const TestSuiteTeamSelectionOption = React.forwardRef<
  HTMLLIElement,
  {
    option: TestSuiteTeamOption
    props: HTMLAttributes<HTMLLIElement>
  }
>(({ option, props, ...rest }, ref) => {
  return (
    <li {...props} {...rest} ref={ref}>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography noWrap>{option.label}</Typography>
        {option.selected && <Typography>Selected</Typography>}
      </Stack>
    </li>
  )
})

export const TestSuiteTeamSelection: React.FC<{
  teams: Team[]
}> = ({ teams }) => {
  const [selectedTeamOption, setSelectedTeamOption] =
    useState<TestSuiteTeamOption>(null)
  const { setValue } = useFormContext<TestSuiteFormValues>()
  const targetTeamIdsValue = useWatch({ name: 'targetTeamIds' })

  const handleSelectTeam = useCallback((_e, newValue) => {
    setSelectedTeamOption(newValue)
  }, [])

  const handleAddTeam = (e) => {
    e.preventDefault()

    if (
      selectedTeamOption &&
      !targetTeamIdsValue.includes(selectedTeamOption.id)
    ) {
      setValue(
        'targetTeamIds',
        [...targetTeamIdsValue, selectedTeamOption.id],
        { shouldDirty: true }
      )
    }
    setSelectedTeamOption(null)
  }

  const teamOptions: TestSuiteTeamOption[] = useMemo(() => {
    const groupedTeams: {
      available: TestSuiteTeamOption[]
      selected: TestSuiteTeamOption[]
    } = (teams ?? []).reduce(
      (groupedTeamsAcc, team) => {
        const selected = targetTeamIdsValue.includes(team.identifier)
        const teamOption = {
          id: team.identifier,
          label: team.name,
          selected
        }

        groupedTeamsAcc[selected ? 'selected' : 'available'].push(teamOption)

        return groupedTeamsAcc
      },
      {
        selected: [],
        available: []
      }
    )

    return groupedTeams.available.concat(
      naturalSortBy(groupedTeams.selected, (o) => o.label)
    )
  }, [targetTeamIdsValue, teams])

  const isDisabled = !selectedTeamOption

  const { trans } = useTranslation()

  return (
    <Stack spacing={4}>
      <Stack>
        <Typography variant="h6">
          {trans('test_suite.forms.test_suite_team_selection.header')}
        </Typography>
        <Typography variant="body1">
          {trans('test_suite.forms.test_suite_team_selection.body')}
        </Typography>
      </Stack>
      <Stack
        alignItems="top"
        direction="row"
        spacing={2}
        sx={{ alignItems: 'flex-start' }}
      >
        <Autocomplete
          disableListWrap
          fullWidth
          getOptionDisabled={(option: TestSuiteTeamOption) => option.selected}
          getOptionLabel={(option: TestSuiteTeamOption) => option.label}
          isOptionEqualToValue={(
            option: TestSuiteTeamOption,
            value: TestSuiteTeamOption
          ) => option.id === value.id}
          ListboxComponent={VirtualizedListboxComponent}
          ListboxProps={{
            OptionComponent: TestSuiteTeamSelectionOption
          }}
          multiple={false}
          options={teamOptions}
          popupIcon={null}
          renderInput={(params) => (
            <TextField
              {...params}
              data-testid="test-suite-team-selection-input"
              helperText="Search for an existing team"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment
                      position="start"
                      sx={{
                        color: 'text.secondary',
                        marginLeft: 1,
                        marginRight: 1
                      }}
                    >
                      <Icon css={{ height: 14 }} icon="magnifyingGlass" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                )
              }}
              label="Search Teams"
            />
          )}
          renderOption={(props, option) => [props, option]}
          value={selectedTeamOption}
          onChange={handleSelectTeam}
        />
        <Button
          data-testid="test-suite-team-selection-add-button"
          disabled={isDisabled}
          size="large"
          startIcon={<Icon icon="plus" />}
          variant="contained"
          onClick={handleAddTeam}
        >
          {trans('add')}
        </Button>
      </Stack>
    </Stack>
  )
}
